import { useLoaderData, useNavigate } from '@remix-run/react';
import { useState, useEffect } from 'react';
import { atom } from 'nanostores';
import type { Message } from 'ai';
import { toast } from 'react-toastify';
import { workbenchStore } from '~/lib/stores/workbench';
import { getFileActions, getMessages, getNextId, getUrlId, openDatabase, setFileActions, setMessages } from './db';
import { selectionStore } from './selectionStore';
import type { StoreFileAction } from '~/types/actions';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

export interface ChatHistoryItem {
  id: string;
  urlId?: string;
  description?: string;
  messages: Message[];
  fileActions: StoreFileAction[];
  timestamp: string;
  netlifyUrl?: string;
  projectRules?: string;
  techStack: { framework: string; language: string };
}

const persistenceEnabled = !import.meta.env.VITE_DISABLE_PERSISTENCE;

export const db = persistenceEnabled ? await openDatabase() : undefined;

export const chatId = atom<string | undefined>(undefined);
export const description = atom<string | undefined>(undefined);
export const urlIdStore = atom<string | undefined>(undefined);

// console.log('description', description.get());

export function useChatHistory() {
  selectionStore.setKey('projectName', 'Untitled');

  const navigate = useNavigate();
  const { id: mixedId } = useLoaderData<{ id?: string }>();

  const [initialMessages, setInitialMessages] = useState<Message[]>([]);
  const [initialFileActions, setInitialFileActions] = useState<StoreFileAction[]>([]);
  const [ready, setReady] = useState<boolean>(false);
  const framework = selectionStore.get().framework;
  const language = selectionStore.get().language;

  //  console.log('[useChatHistory] framework & language:', framework, language);

  useEffect(() => {
    if (!db) {
      setReady(true);

      if (persistenceEnabled) {
        toast.error(`Chat persistence is unavailable`);
      }

      return;
    }

    if (mixedId) {
      getMessages(db, mixedId)
        .then((storedMessages) => {
          console.log('[history] storedMessages:', storedMessages);

          if (storedMessages && storedMessages.messages.length > 0) {
            setInitialMessages(storedMessages.messages);
            setInitialFileActions(storedMessages.fileActions);
            urlIdStore.set(storedMessages.urlId);
            description.set(storedMessages.description);
            chatId.set(storedMessages.id);
          } else {
            navigate(`/`, { replace: true });
          }

          setReady(true);
        })
        .catch((error) => {
          /*
           * Sentry.captureException(error, {
           *   extra: {
           *     message: 'Error loading chat history',
           *     mixedId,
           *   },
           * });
           */
          toast.error(error.message);
        });
    }
  }, []);

  const getData = async () => {
    const { firstAIArtifact } = workbenchStore;
    console.log('[useChatHistory] firstAIArtifact:', firstAIArtifact);
    console.log('[useChatHistory] urlId:', urlIdStore.get());

    if (!urlIdStore.get() && firstAIArtifact?.id) {
      const newUrlId = await getUrlId(db as IDBDatabase, firstAIArtifact.id);
      console.log('[useChatHistory] newUrlId:', newUrlId);

      navigateChat(newUrlId);
      urlIdStore.set(newUrlId);
    }

    if (!description.get() && firstAIArtifact?.title) {
      description.set(firstAIArtifact?.title);
    }

    if (initialMessages.length === 0 && !chatId.get()) {
      const nextId = await getNextId(db as IDBDatabase);

      chatId.set(nextId);

      if (!urlIdStore.get()) {
        navigateChat(nextId);
      }
    }
  };

  return {
    ready: !mixedId || ready,
    initialMessages,
    initialFileActions,
    storeMessageHistory: async (messages: Message[], fileActions: StoreFileAction[]) => {
      if (!db || messages.length === 0) {
        return;
      }

      await getData();
      console.log('[useChatHistory] storeMessageHistory called:', urlIdStore.get());
      await setMessages(
        db as IDBDatabase,
        chatId.get() as string,
        messages,
        fileActions,
        framework,
        language,
        urlIdStore.get(),
        description.get(),
      );
      console.log('[refresh] storeMessageHistory setMessages called:', fileActions);
    },
    storeFileActions: async (fileActions: StoreFileAction[]) => {
      if (!db || fileActions.length === 0) {
        return;
      }

      await getData();
      await setFileActions(db as IDBDatabase, chatId.get() as string, fileActions, urlIdStore.get());
    },
    getFileActions: async () => {
      if (!db) {
        return [];
      }

      return await getFileActions(db as IDBDatabase, chatId.get() as string);
    },
  };
}

function navigateChat(nextId: string) {
  /**
   * FIXME: Using the intended navigate function causes a rerender for <Chat /> that breaks the app.
   *
   * `navigate(`/chat/${nextId}`, { replace: true });`
   */
  const url = new URL(window.location.href);
  url.pathname = `/chat/${nextId}`;
  window.history.replaceState({}, '', url);
}
